import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/shared/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useContentCounts } from "@/hooks/useContentCounts";
import { BlogManager } from "./BlogManager";
import { TestimonialsManager } from "./TestimonialsManager";
import { ProjectManager } from "./ProjectManager";
import { LinksManager } from "./LinksManager";
import { CategoryManager } from "./CategoryManager";
import { ProductsCatalogManager } from "./ProductsCatalogManager";

type ContentTab = "blog" | "depoimentos" | "produtos" | "catalogo" | "links" | "categorias";

function resolveTab(param: string | null): ContentTab {
  if (
    param === "blog" ||
    param === "depoimentos" ||
    param === "produtos" ||
    param === "catalogo" ||
    param === "links" ||
    param === "categorias"
  ) {
    return param;
  }
  return "blog";
}

const SEG_TRIGGER =
  "rounded-lg px-4 py-2 text-[12.5px] font-semibold text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm";

export function ContentHub() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTab(searchParams.get("tab"));
  const { data: counts } = useContentCounts();

  const handleTabChange = (value: string) => {
    const tab = resolveTab(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    });
  };

  const label = (base: string, n?: number) => (n === undefined ? base : `${base} · ${n}`);

  return (
    <div className="mx-auto flex max-w-[1100px] flex-col gap-[22px]">
      <PageHeader
        title="Conteúdo"
        description="Blog, depoimentos, portfólio, catálogo de produtos e links da bio"
        actions={
          <Button
            className="h-[38px] rounded-[10px] px-4 text-[13px] font-semibold"
            onClick={() => navigate("/admin/blog/new")}
          >
            Novo post
          </Button>
        }
      />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col gap-[22px]">
        <TabsList className="h-auto w-fit flex-wrap gap-[3px] rounded-[10px] bg-muted p-[3px]">
          <TabsTrigger value="blog" className={SEG_TRIGGER}>
            {label("Blog", counts?.blog)}
          </TabsTrigger>
          <TabsTrigger value="depoimentos" className={SEG_TRIGGER}>
            {label("Depoimentos", counts?.depoimentos)}
          </TabsTrigger>
          <TabsTrigger value="produtos" className={SEG_TRIGGER}>
            {label("Cases", counts?.produtos)}
          </TabsTrigger>
          <TabsTrigger value="catalogo" className={SEG_TRIGGER}>
            {label("Catálogo", counts?.catalogo)}
          </TabsTrigger>
          <TabsTrigger value="links" className={SEG_TRIGGER}>
            {label("Links (Bio)", counts?.links)}
          </TabsTrigger>
          <TabsTrigger value="categorias" className={SEG_TRIGGER}>
            {label("Categorias", counts?.categorias)}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="blog" className="mt-0">
          <BlogManager embedded />
        </TabsContent>
        <TabsContent value="depoimentos" className="mt-0">
          <TestimonialsManager embedded />
        </TabsContent>
        <TabsContent value="produtos" className="mt-0">
          <ProjectManager embedded />
        </TabsContent>
        <TabsContent value="catalogo" className="mt-0">
          <ProductsCatalogManager embedded />
        </TabsContent>
        <TabsContent value="links" className="mt-0">
          <LinksManager embedded />
        </TabsContent>
        <TabsContent value="categorias" className="mt-0">
          <CategoryManager embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
